// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Label,
  CartesianGrid,
  ResponsiveContainer,
  Customized,
  Text,
  Tooltip,
} from "recharts";

// Components
import Box from "@mui/material/Box";
import CustomDot from "./CustomDot/CustomDot";
import CustomTooltip from "./CustomTooltip/CustomTooltip";
import ProgressionLineChartsLegend from "./ProgressionLineChartsLegend/ProgressionLineChartsLegend";

type ProgressionLineChartsType = {
  chartData: Array<{
    age: string | number;
    time: string;
    MD: string | number | null;
    PSD: string | number | null;
    VRFI: string | number | null;
    pointShape: string;
    isRetinaLogikExam: boolean;
  }>;
  chartHeight?: number;
  displayHorizontally?: boolean;
};

const ProgressionLineCharts = ({
  chartData,
  chartHeight = 270,
  displayHorizontally = false,
}: ProgressionLineChartsType) => {
  const { t } = useTranslation();

  const hasNoEntries = useMemo(
    () => chartData.length === 0,
    [chartData.length]
  );

  const mdRange = useMemo(() => {
    const newChart = [...chartData];
    const sortedChart = newChart?.sort((a, b) => Number(a.MD) - Number(b.MD));
    return [
      Number(sortedChart[0]?.MD),
      Number(sortedChart[sortedChart.length - 1]?.MD),
    ];
  }, [chartData]);

  const psdRange = useMemo(() => {
    const newChart = [...chartData];
    const sortedChart = newChart?.sort((a, b) => Number(a.PSD) - Number(b.PSD));
    return [
      Number(sortedChart[0]?.PSD),
      Number(sortedChart[sortedChart.length - 1]?.PSD),
    ];
  }, [chartData]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "fit-content",
        gap: "20px",
      }}
    >
      <ProgressionLineChartsLegend />
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: displayHorizontally ? "row" : "column",
          rowGap: "20px",
          columnGap: "0px",
        }}
      >
        <ResponsiveContainer width="100%" height={chartHeight}>
          <LineChart
            width={500}
            height={200}
            data={chartData}
            syncId="md-chart"
            margin={{
              top: 10,
              right: 35,
              left: hasNoEntries ? -25 : 5,
              bottom: 60,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="time"
              interval={0}
              textAnchor="end"
              angle={-40}
              fontSize={12}
            >
              <Label value={t("Time")} position="insideBottom" dy={60} />
            </XAxis>

            <YAxis
              fontSize={12}
              tickFormatter={(value) => value.toFixed(1)}
              allowDecimals={true}
              domain={mdRange}
              type="number"
            >
              <Label
                value="MD (dB)"
                position="insideLeft"
                angle={-90}
                style={{ textAnchor: "middle" }}
              />
            </YAxis>

            <Tooltip content={<CustomTooltip units="dB" />} />

            <Customized
              component={() => {
                return hasNoEntries ? (
                  <Text
                    style={{
                      fontSize: "14px",
                      transform: `translate(50%, 50%)`,
                    }}
                    y={-40}
                    textAnchor="middle"
                    verticalAnchor="middle"
                  >
                    {t("No Data Available.")}
                  </Text>
                ) : null;
              }}
            />
            <Line
              type="monotone"
              dataKey="MD"
              stroke="#000"
              fill="#000"
              dot={<CustomDot />}
              activeDot={<CustomDot />}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>

        <ResponsiveContainer width="100%" height={chartHeight}>
          <LineChart
            width={500}
            height={200}
            data={chartData}
            syncId="psd-chart"
            margin={{
              top: 10,
              right: 35,
              left: hasNoEntries ? -25 : 5,
              bottom: 60,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="time"
              interval={0}
              textAnchor="end"
              angle={-40}
              fontSize={12}
            >
              <Label value={t("Time")} position="insideBottom" dy={60} />
            </XAxis>

            <YAxis
              fontSize={12}
              tickFormatter={(value) => value.toFixed(1)}
              allowDecimals={true}
              domain={psdRange}
              type="number"
            >
              <Label
                value="PSD (dB)"
                position="insideLeft"
                angle={-90}
                style={{ textAnchor: "middle" }}
              />
            </YAxis>

            <Tooltip content={<CustomTooltip units="dB" />} />

            <Customized
              component={() => {
                return hasNoEntries ? (
                  <Text
                    style={{
                      fontSize: "14px",
                      transform: `translate(50%, 50%)`,
                    }}
                    y={-40}
                    textAnchor="middle"
                    verticalAnchor="middle"
                  >
                    {t("No Data Available.")}
                  </Text>
                ) : null;
              }}
            />
            <Line
              type="monotone"
              dataKey="PSD"
              stroke="#000"
              fill="#000"
              dot={<CustomDot />}
              activeDot={<CustomDot />}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>

        <ResponsiveContainer width="100%" height={chartHeight}>
          <LineChart
            width={500}
            height={200}
            data={chartData}
            syncId="vrfi-chart"
            margin={{
              top: 10,
              right: 35,
              left: hasNoEntries ? -25 : 5,
              bottom: 60,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="age"
              interval={0}
              textAnchor="end"
              angle={-40}
              fontSize={12}
            >
              <Label value={t("Age")} position="insideBottom" dy={60} />
            </XAxis>

            <YAxis
              fontSize={12}
              ticks={hasNoEntries ? [] : [0, 20, 40, 60, 80, 100]}
              domain={hasNoEntries ? [] : [0, 100]}
              type="number"
            >
              <Label
                value="VRFI (%)"
                position="insideLeft"
                angle={-90}
                style={{ textAnchor: "middle" }}
              />
            </YAxis>

            <Tooltip content={<CustomTooltip units="%" />} />

            <Customized
              component={() => {
                return hasNoEntries ? (
                  <Text
                    style={{
                      fontSize: "14px",
                      transform: `translate(50%, 50%)`,
                    }}
                    y={-40}
                    textAnchor="middle"
                    verticalAnchor="middle"
                  >
                    {t("No Data Available.")}
                  </Text>
                ) : null;
              }}
            />
            <Line
              type="monotone"
              dataKey="VRFI"
              stroke="#000"
              fill="#000"
              dot={<CustomDot />}
              activeDot={<CustomDot />}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Box>
  );
};

export default ProgressionLineCharts;
